import { create } from 'zustand';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { InstructorProfile } from '../services/instructorService';

interface InstructorAuthState {
  token: string | null;
  user: User | null;
  instructor: InstructorProfile | null;
  permissions: string[];
  isAuthenticated: boolean;
  isInstructor: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  loadInstructorProfile: () => Promise<void>;
  loadPermissions: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  checkInstructorStatus: (userId: string) => Promise<boolean>;
}

export const useInstructorAuth = create<InstructorAuthState>((set, get) => ({
  token: null,
  user: null,
  instructor: null,
  permissions: [],
  isAuthenticated: false,
  isInstructor: false,
  isLoading: true,

  /**
   * Vérifie si un utilisateur est un instructeur actif
   */
  checkInstructorStatus: async (userId: string): Promise<boolean> => {
    try {
      // Vérifier d'abord si l'utilisateur existe et a le rôle instructor
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('id, role, is_active')
        .eq('id', userId)
        .single();

      if (userError || !user || user.role !== 'instructor' || !user.is_active) {
        return false;
      }

      // Vérifier si l'instructeur existe dans la table elearn_instructors
      const { data: instructor, error: instructorError } = await supabase
        .from('elearn_instructors')
        .select('id, is_active')
        .eq('user_id', userId)
        .single();

      if (instructorError || !instructor || !instructor.is_active) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erreur lors de la vérification du statut instructeur:', error);
      return false;
    }
  },

  /**
   * Charge le profil de l'instructeur
   */
  loadInstructorProfile: async (): Promise<void> => {
    const { user } = get();
    if (!user) return;

    try {
      const { data: instructor, error } = await supabase
        .from('elearn_instructors')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Erreur lors du chargement du profil instructeur:', error);
        return;
      }

      set({ instructor, isInstructor: true });
    } catch (error) {
      console.error('Exception lors du chargement du profil instructeur:', error);
    }
  },

  /**
   * Charge les permissions de l'instructeur
   */
  loadPermissions: async (): Promise<void> => {
    const { instructor } = get();
    if (!instructor) return;

    try {
      const { data: permissions, error } = await supabase
        .from('elearn_instructor_permissions')
        .select('permission_type')
        .eq('instructor_id', instructor.id)
        .eq('is_granted', true);

      if (error) {
        console.error('Erreur lors du chargement des permissions:', error);
        return;
      }

      const permissionsList = permissions?.map(p => p.permission_type) || [];
      set({ permissions: permissionsList });
    } catch (error) {
      console.error('Exception lors du chargement des permissions:', error);
    }
  },

  /**
   * Vérifie si l'instructeur a une permission spécifique
   */
  hasPermission: (permission: string): boolean => {
    const { permissions, isInstructor } = get();
    if (!isInstructor) return false;
    return permissions.includes(permission);
  },

  /**
   * Connexion d'un instructeur
   */
  login: async (token, user) => {
    set({ isLoading: true });

    try {
      // Vérifier si l'utilisateur est un instructeur actif
      const isInstructorActive = await get().checkInstructorStatus(user.id);
      
      if (!isInstructorActive) {
        await supabase.auth.signOut();
        throw new Error('Accès refusé. Vous n\'êtes pas autorisé en tant qu\'instructeur ou votre compte est inactif.');
      }

      // Stocker les informations d'authentification
      set({ 
        token, 
        user, 
        isAuthenticated: true,
        isLoading: false 
      });

      // Charger le profil instructeur et les permissions
      await get().loadInstructorProfile();
      await get().loadPermissions();

      // Stocker le token dans le localStorage
      localStorage.setItem('instructor_token', token);
      localStorage.setItem('instructor_user', JSON.stringify(user));

    } catch (error) {
      set({ 
        token: null, 
        user: null, 
        instructor: null,
        permissions: [],
        isAuthenticated: false, 
        isInstructor: false,
        isLoading: false 
      });
      throw error;
    }
  },

  /**
   * Déconnexion
   */
  logout: async () => {
    try {
      await supabase.auth.signOut();
      
      // Nettoyer le localStorage
      localStorage.removeItem('instructor_token');
      localStorage.removeItem('instructor_user');
      
      set({ 
        token: null, 
        user: null, 
        instructor: null,
        permissions: [],
        isAuthenticated: false, 
        isInstructor: false,
        isLoading: false 
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  },

  /**
   * Vérification de l'authentification au chargement
   */
  checkAuth: async (): Promise<boolean> => {
    set({ isLoading: true });

    try {
      // Vérifier la session Supabase
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session?.user) {
        // Vérifier le localStorage comme fallback
        const storedToken = localStorage.getItem('instructor_token');
        const storedUser = localStorage.getItem('instructor_user');
        
        if (!storedToken || !storedUser) {
          set({ isLoading: false });
          return false;
        }

        const user = JSON.parse(storedUser);
        
        // Vérifier si l'utilisateur est toujours un instructeur actif
        const isInstructorActive = await get().checkInstructorStatus(user.id);
        
        if (!isInstructorActive) {
          await get().logout();
          return false;
        }

        set({ 
          token: storedToken, 
          user, 
          isAuthenticated: true 
        });
      } else {
        // Vérifier si l'utilisateur de la session est un instructeur actif
        const isInstructorActive = await get().checkInstructorStatus(session.user.id);
        
        if (!isInstructorActive) {
          await get().logout();
          return false;
        }

        set({ 
          token: session.access_token, 
          user: session.user, 
          isAuthenticated: true 
        });
      }

      // Charger le profil instructeur et les permissions
      await get().loadInstructorProfile();
      await get().loadPermissions();

      set({ isLoading: false });
      return true;

    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification:', error);
      set({ 
        token: null, 
        user: null, 
        instructor: null,
        permissions: [],
        isAuthenticated: false, 
        isInstructor: false,
        isLoading: false 
      });
      return false;
    }
  }
}));

// Hook pour vérifier l'authentification au montage de l'application
import React from 'react';

export const useInstructorAuthInit = () => {
  const checkAuth = useInstructorAuth(state => state.checkAuth);
  const isLoading = useInstructorAuth(state => state.isLoading);

  React.useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return { isLoading };
};

export default useInstructorAuth;
